import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import LanguageSelector from '../ui/LanguageSelector';
import MobileNav from '../ui/MobileNav';
import AppIcon from '../ui/AppIcon';
import { 
  Menu as MenuIcon,
  X as CloseIcon,
  Home as HomeIcon,
  Grid as CropsIcon,
  Calendar as TasksIcon,
  Package as ResourcesIcon,
  TrendingUp as SalesIcon,
  Users as VendorsIcon,
  Tool as ToolIcon,
  LogOut as LogOutIcon
} from 'react-feather';

interface NavItem {
  path: string;
  label: string;
  icon: React.FC<{ className?: string }>;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems: NavItem[] = [
    { path: '/dashboard', label: t('dashboard.overview'), icon: HomeIcon },
    { path: '/dashboard/crops', label: t('crops.title'), icon: CropsIcon },
    { path: '/dashboard/tasks', label: t('tasks.title'), icon: TasksIcon },
    { path: '/dashboard/equipment', label: t('equipment.title'), icon: ToolIcon },
    { path: '/dashboard/resources', label: t('resources.title'), icon: ResourcesIcon },
    { path: '/dashboard/sales', label: t('sales.title'), icon: SalesIcon },
    { path: '/dashboard/vendors', label: t('vendors.title'), icon: VendorsIcon }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 z-50">
        <div className="flex flex-col flex-grow bg-white shadow-xl border-r border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600">
            <div className="flex items-center gap-3">
              <AppIcon size="md" />
              <h1 className="text-2xl font-bold text-white">AgroTrack</h1>
            </div>
          </div>
          
          {/* Language Selector */}
          <div className="px-6 py-4 border-b border-gray-100">
            <LanguageSelector />
          </div>

          {/* Navigation */}
          <div className="flex-grow flex flex-col pt-6 pb-4 overflow-y-auto">
            <nav className="flex-1 px-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${
                      isActive
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-r-4 border-green-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ease-in-out transform hover:scale-105`}
                  >
                    <Icon className={`mr-4 h-6 w-6 ${
                      isActive ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
                    }`} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            
            {/* Logout Button */}
            <div className="px-4 mt-auto">
              <Button
                onClick={handleLogout}
                variant="secondary"
                className="w-full justify-start py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-gray-200"
              >
                <LogOutIcon className="mr-4 h-5 w-5" />
                {t('auth.logout')}
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow-lg border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <AppIcon size="sm" />
            <h1 className="text-xl font-bold text-green-600">AgroTrack</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <LanguageSelector />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 transition-colors duration-200"
              aria-label={isMobileMenuOpen ? t('common.close') : t('common.menu')}
            >
              {isMobileMenuOpen ? (
                <CloseIcon className="block h-6 w-6" />
              ) : (
                <MenuIcon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <MobileNav
          navItems={navItems}
          onClose={() => setIsMobileMenuOpen(false)}
          activePath={location.pathname}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:pl-72">
        <main className="flex-1 p-6 mt-16 lg:mt-0">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;